import { useEffect } from "react";
import { registrarVistaSeleccion } from "@/lib/selecciones.functions";
import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { buildSeo, SEO_ORIGIN, operacionLabel } from "@/lib/seo";

import { getLocalSeoBundle } from "@/lib/anuncios.functions";
import { Button } from "@/components/ui/button";
import {
  AnuncioFicha,
  buildSeoTitleAndH1,
  buildFacetPath,
  formatPriceEs,
  priceForOp,
  truncate,
} from "@/components/portal/AnuncioFicha";

// ─── Helpers SEO ─────────────────────────────────────────────
type SeoBundle = Awaited<ReturnType<typeof getLocalSeoBundle>>;

function buildSeoDescription(b: any, precioFmt: string | null): string {
  const anuncio = b.anuncio;
  const tipoNombre = b.uso?.nombre ?? b.tipo_local?.nombre ?? "Local";
  const opLower = operacionLabel(b.operacion?.slug ?? "") || "traspaso, venta o alquiler";
  const distrito = b.barrio?.nombre ?? null;
  const ciudad = b.ciudad?.nombre ?? null;
  const loc = [distrito, ciudad].filter(Boolean).join(", ");
  const m2 = anuncio.t_superficie != null ? ` de ${anuncio.t_superficie} m²` : "";
  const highlights: string[] = [];
  if (b.licencia?.nombre) highlights.push(`licencia ${b.licencia.nombre}`);
  if (anuncio.h_terraza) highlights.push("terraza");
  if (anuncio.h_salida_humos) highlights.push("salida de humos");
  if (anuncio.facturacion_anual)
    highlights.push(`facturación ${new Intl.NumberFormat("es-ES").format(anuncio.facturacion_anual)} €/año`);
  const hl = highlights.slice(0, 2).join(", ");
  const precios: string[] = [];
  if (anuncio.precio_traspaso != null) precios.push(`Traspaso ${formatPriceEs(anuncio.precio_traspaso)}`);
  if (anuncio.precio_venta != null) precios.push(`Venta ${formatPriceEs(anuncio.precio_venta)}`);
  if (anuncio.precio_alquiler != null) precios.push(`Alquiler ${formatPriceEs(anuncio.precio_alquiler, "alquiler")}`);
  const precioStr = precios.length ? precios.join(", ") + "." : precioFmt ? `${precioFmt}.` : "";
  const ref = anuncio.referencia ? ` Ref. ${anuncio.referencia}.` : "";
  let desc = `${tipoNombre}${m2} en ${opLower}${loc ? ` en ${loc}` : ""}.${hl ? ` ${hl.charAt(0).toUpperCase() + hl.slice(1)}.` : ""} ${precioStr}${ref}`;
  desc = desc.replace(/\s+/g, " ").trim();
  if (desc.length > 160) desc = truncate(desc, 158);
  if (desc.length < 100) {
    desc += ` Contacta al anunciante en Bizaclia, el portal inmobiliario para negocios.`;
    if (desc.length > 160) desc = truncate(desc, 158);
  }
  return desc;
}

function buildBreadcrumbsForLocal(b: any, listingTitle: string) {
  const items: Array<{ name: string; url: string }> = [
    { name: "Inicio", url: `${SEO_ORIGIN}/` },
    { name: "Portal", url: `${SEO_ORIGIN}/portal` },
  ];
  const op = b.operacion;
  const uso = b.uso;
  const prov = b.provincia;
  const ciudad = b.ciudad;
  const barrio = b.barrio;
  if (op?.slug && op?.nombre) items.push({ name: op.nombre, url: `${SEO_ORIGIN}${buildFacetPath(op.slug, null, null, null, null)}` });
  if (ciudad?.nombre) items.push({ name: ciudad.nombre, url: `${SEO_ORIGIN}${buildFacetPath(op?.slug ?? null, uso?.slug ?? null, prov?.slug ?? null, ciudad.slug ?? null, null)}` });
  if (barrio?.nombre) items.push({ name: barrio.nombre, url: `${SEO_ORIGIN}${buildFacetPath(op?.slug ?? null, uso?.slug ?? null, prov?.slug ?? null, ciudad?.slug ?? null, barrio.slug ?? null)}` });
  items.push({ name: listingTitle, url: `${SEO_ORIGIN}/local/${b.anuncio.id}` });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

function buildRealEstateJsonLd(b: any, title: string, description: string, imageUrl: string | null) {
  const opSlug = b.operacion?.slug ?? null;
  const { value: precio } = priceForOp(b.anuncio, opSlug);
  const address: Record<string, string> = { "@type": "PostalAddress", addressCountry: "ES" };
  if (b.ciudad?.nombre) address.addressLocality = b.ciudad.nombre;
  if (b.provincia?.nombre) address.addressRegion = b.provincia.nombre;
  const doc: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: title.replace(/\s*\|\s*Bizaclia$/, ""),
    url: `${SEO_ORIGIN}/local/${b.anuncio.id}`,
    description,
  };
  if (imageUrl) doc.image = [imageUrl];
  if (b.anuncio.created_at) doc.datePosted = new Date(b.anuncio.created_at).toISOString();
  if (precio != null) {
    doc.offers = {
      "@type": "Offer",
      price: precio,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: `${SEO_ORIGIN}/local/${b.anuncio.id}`,
    };
  }
  if (Object.keys(address).length > 2) doc.address = address;
  if (b.anuncio.t_superficie != null) {
    doc.floorSize = { "@type": "QuantitativeValue", value: b.anuncio.t_superficie, unitCode: "MTK" };
  }
  return doc;
}

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/local/$id")({
  validateSearch: (s: Record<string, unknown>) => ({
    demanda: typeof s.demanda === "string" ? s.demanda : undefined,
    /** Selección de un agente desde la que se abrió la ficha. */
    sel: typeof s.sel === "string" ? s.sel : undefined,
  }),
  loader: async ({ params }) => {
    const bundle = (await getLocalSeoBundle({ data: { id: params.id } })) as SeoBundle;
    if (!bundle || bundle.status === "missing") throw notFound();
    return bundle;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return buildSeo({
        title: "Anuncio no disponible · Bizaclia",
        description: "Este anuncio no está disponible.",
        path: `/local/${params.id}`,
        noindex: true,
      });
    }
    const b = loaderData;
    const path = `/local/${params.id}`;
    if (b.status === "inactive") {
      return buildSeo({
        title: "Este anuncio ya no está disponible | Bizaclia",
        description: "Este anuncio ya no está disponible. Explora locales similares en traspaso, venta o alquiler en Bizaclia.",
        path,
        noindex: true,
        image: b.coverUrl ?? undefined,
      });
    }
    const meta = buildSeoTitleAndH1(b);
    const description = buildSeoDescription(b, meta.precioFmt);
    const jsonLd = [
      buildRealEstateJsonLd(b, meta.title, description, b.coverUrl),
      buildBreadcrumbsForLocal(b, meta.h1),
    ];
    return buildSeo({
      title: meta.title,
      description,
      path,
      type: "article",
      image: b.coverUrl ?? undefined,
      imageAlt: b.coverAlt ?? meta.h1,
      jsonLd,
    });
  },
  component: LocalPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <main className="container mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold mb-2">No se pudo cargar el anuncio</h1>
        <p className="text-muted-foreground mb-6">{error.message}</p>
        <Button onClick={() => { reset(); router.invalidate(); }}>Reintentar</Button>
      </main>
    );
  },
  notFoundComponent: () => (
    <main className="container mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold mb-2">Anuncio no encontrado</h1>
      <p className="text-muted-foreground mb-6">El anuncio que buscas no existe o ha sido eliminado.</p>
      <Link to="/portal" className="text-primary underline">Volver al portal</Link>
    </main>
  ),
});

function LocalPage() {
  const { id } = Route.useParams();
  const { demanda, sel } = Route.useSearch();
  const bundle = Route.useLoaderData() as any;
  useEffect(() => {
    if (sel) void registrarVistaSeleccion({ data: { token: sel, anuncio_id: id } }).catch(() => {});
  }, [sel, id]);
  return <AnuncioFicha id={id} bundle={bundle} demandaSearch={demanda} />;
}
